/**
 * LiveCommentsPanel — always-visible comments column on the event detail page.
 *
 * Replaces the legacy slide-in `AnnouncementChatPanel` ("Начать обсуждение"
 * capsule + modal). The panel is mounted directly in the page layout — no
 * tap-to-open, no separate screen — so the discussion is part of the event,
 * not hidden behind a CTA.
 *
 * Three input states, gated in this order:
 *
 *   1. Guest (no auth)              → "Войдите, чтобы написать" tap → onAuthRequired
 *   2. Authed, not in circle (!canWrite) → orange CTA "Хотите Обсудить?
 *                                          Присоединяйтесь :)" → JoinEventModal
 *                                          → api.subscribeToMaster (one
 *                                          round-trip: subscribe + circle
 *                                          add) → composer unlocks
 *   3. In circle (canWrite)         → message composer
 *
 * CORE-1725 — owner of the circle (own `masterProfileId` from /me equals the
 * event's) is state 3 without a subscription and never sees the CTA or the
 * modal: subscribing to yourself is refused by the server. While membership
 * is still unknown neither the CTA nor the composer is shown.
 *
 * Frozen/ended events get a read-only banner instead of a composer.
 *
 * Backed by `useEventThread` — the same hook AnnouncementChatPanel used, so
 * messages, participant tracking and real-time updates are unchanged.
 */

import React, { useState, useCallback } from 'react';
import {
  View,
  StyleSheet,
  Platform,
  TouchableOpacity,
  Image,
  TextInput,
  ScrollView,
  ViewStyle,
  TextStyle,
  ImageStyle,
} from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';
import { Typography } from './ui';
import { fonts } from '../styles';
import { useTheme } from '../context/ThemeContext';
import { useEventThread, EventMeta } from '../hooks/useEventThread';
import { JoinEventModal } from './chat/JoinEventModal';
import { api } from '../services/api';
import { friendlyApiError } from '../services/errorMessages';
import { fontWeights } from '../styles/typographyTokens';
import { isSelfSubscriptionRefusal, resolveDiscussionFooter } from '../utils/eventDiscussionAccess';

import { ACCENT_TEXT, ON_ACCENT_TEXT } from '../styles/accentText';
import { surfaceFill } from '../styles/platformBackgrounds';
export type LiveCommentsPanelProps = {
  /**
   * Event metadata used to bind to the Stream Chat thread. When null
   * (or the user is unauthenticated), the panel renders an empty state
   * with the appropriate auth/join CTA — never fake messages.
   */
  meta: EventMeta | null;
  /** Master display name — surfaced inside the join modal. */
  masterName?: string | null;
  /** Backend-tracked subscription state — drives the `canWrite` derivation. */
  isSubscribed?: boolean;
  isAuthenticated?: boolean;

  onAuthRequired?: () => void;
  /**
   * Optional hook fired AFTER subscribeToMaster resolves. The detail screen
   * uses it to flip its local `isSubscribed` state so the "Follow" button and
   * the hero CTA stay in sync without an extra round-trip.
   */
  onAfterJoin?: () => void;
};

export const LiveCommentsPanel: React.FC<LiveCommentsPanelProps> = ({
  meta,
  masterName,
  isSubscribed = false,
  isAuthenticated = false,
  onAuthRequired,
  onAfterJoin,
}) => {
  const { isDark, colors: tc } = useTheme();
  const [draft, setDraft] = useState('');
  const [sendError, setSendError] = useState<string | null>(null);
  const [joinVisible, setJoinVisible] = useState(false);
  const [joinError, setJoinError] = useState<string | null>(null);
  // Bumped after a successful subscribeToMaster — forces the thread hook
  // to re-watch the channel so canWrite flips from false to true. Without
  // this, Stream's member.added event is the only path to refresh, and it
  // only fires if we were already watching the channel as an observer.
  const [reloadTick, setReloadTick] = useState(0);

  // Stream-Chat-backed thread — gated to authed users with valid meta.
  const thread = useEventThread(meta, {
    enabled: !!meta && isAuthenticated,
    isRsvped: isSubscribed,
    reloadTick,
  });

  const messages = thread.messages;
  const pinnedMessage = thread.pinnedMessage;
  const isFrozen = thread.isFrozen;
  const canWrite = thread.canWrite;
  const isOwner = thread.isOwner;
  // CORE-620 (актуализация 27.10) — готовность обсуждения решает раньше прав:
  // без карточки ни поля ввода, ни предложения подписаться.
  const footer = resolveDiscussionFooter({
    isFrozen, isAuthenticated, membership: thread.membership, readiness: thread.readiness,
  });

  // ─── Theme tokens — light by default to match the meta column it sits
  // beside; falls back to a dark glassy surface in dark mode for contrast
  // with the rest of the screen.
  //
  // CORE-1733 (возврат QA 23.10) — на вебе колонка — поверхность v4 (была
  // плоская #F2F0EE), текст — токены темы: приглушённый rgba(15,20,25,.55)
  // давал 3.95–4.1:1 («Здесь пока тихо», «Войдите, чтобы написать»). Поле ввода
  // внутри поверхности — вложенный блок (surfaceSecondary), а не второй белый
  // прямоугольник. Натив — прежняя палитра.
  const WEB = Platform.OS === 'web';
  const surface = isDark ? 'rgba(255,255,255,0.06)' : '#F2F0EE';
  const surfaceBorder = isDark ? 'rgba(255,255,255,0.10)' : 'rgba(15,20,25,0.08)';
  const textPrimary = WEB ? tc.textPrimary : (isDark ? '#FFFFFF' : '#0F1419');
  const textSecondary = WEB ? tc.textMuted : (isDark ? 'rgba(255,255,255,0.65)' : 'rgba(15,20,25,0.55)');
  const inputSurface = WEB ? tc.surfaceSecondary : (isDark ? 'rgba(255,255,255,0.06)' : '#FFFFFF');
  const inputBorder = WEB ? tc.border : (isDark ? 'rgba(255,255,255,0.10)' : 'rgba(15,20,25,0.10)');
  const panelFill = WEB ? surfaceFill(isDark ? 'dark' : 'light') : { backgroundColor: surface, borderColor: surfaceBorder };

  const handleSend = useCallback(async () => {
    const text = draft.trim();
    if (!text) return;
    setSendError(null);
    if (!isAuthenticated) {
      onAuthRequired?.();
      return;
    }
    if (!canWrite) return;
    try {
      await thread.sendMessage(text);
      setDraft('');
    } catch (err: any) {
      setSendError(friendlyApiError(err));
    }
  }, [draft, isAuthenticated, canWrite, thread, onAuthRequired]);

  const handleJoinPress = useCallback(() => {
    setJoinError(null);
    if (!isAuthenticated) {
      onAuthRequired?.();
      return;
    }
    // CORE-1725 — владельцу подписываться не на кого: своё обсуждение ему уже открыто.
    if (isOwner) return;
    setJoinVisible(true);
  }, [isAuthenticated, isOwner, onAuthRequired]);

  const handleJoinConfirm = useCallback(async () => {
    if (!meta || isOwner) return;
    try {
      // Single backend round-trip: subscribes to master AND adds the user
      // to the master's Stream circle channel (see
      // MasterSubscriptionController.SubscribeAsync → AddCircleMemberAsync).
      await api.subscribeToMaster(meta.masterProfileId);
      setJoinVisible(false);
      onAfterJoin?.();
      // Force the thread hook to re-watch the channel. The backend has
      // just added us as a member, but our local channel state is still
      // the pre-join snapshot — bump the tick so the useEffect re-runs
      // and `canWrite` flips to true once the new watch resolves.
      setReloadTick((t) => t + 1);
    } catch (err: any) {
      // CORE-1725 — сервер узнал во мне владельца (CTA могла остаться, если
      // свой masterProfileId не доехал, а запрос членства упал). Это не сбой:
      // окно закрывается, членство перечитывается, и сервер отвечает «участник».
      if (isSelfSubscriptionRefusal(err)) {
        setJoinVisible(false);
        setReloadTick((t) => t + 1);
        return;
      }
      setJoinError(err?.message || 'Не удалось присоединиться');
      // Keep the modal open so the user sees the inline error and can retry.
    }
  }, [meta, isOwner, onAfterJoin]);

  // ─── Input footer renderer — three states + frozen banner.
  const renderFooter = (): React.ReactNode => {
    if (footer === 'pending') return null;
    if (footer === 'frozen') {
      return (
        <View style={[styles.banner, { backgroundColor: inputSurface, borderColor: inputBorder }]}>
          <Ionicons name="lock-closed" size={14} color={textSecondary} />
          <Typography style={[styles.bannerText, { color: textSecondary }]}>
            Обсуждение закрыто — Live-сессия завершена
          </Typography>
        </View>
      );
    }
    if (footer === 'unavailable') {
      return (
        <View
          testID="event-discussion-unavailable"
          style={[styles.banner, { backgroundColor: inputSurface, borderColor: inputBorder }]}
        >
          <Ionicons name="chatbubbles-outline" size={14} color={textSecondary} />
          <Typography style={[styles.bannerText, { color: textSecondary }]}>
            {thread.status === 'error'
              ? 'Не удалось загрузить обсуждение. Обновите страницу позже.'
              : 'Обсуждение этой Live-сессии пока недоступно.'}
          </Typography>
        </View>
      );
    }
    if (footer === 'signin') {
      return (
        <TouchableOpacity
          activeOpacity={0.9}
          onPress={onAuthRequired}
          style={[styles.banner, { backgroundColor: inputSurface, borderColor: inputBorder }]}
        >
          <Ionicons name="lock-closed-outline" size={14} color={textSecondary} />
          <Typography style={[styles.bannerText, { color: textSecondary }]}>
            Войдите, чтобы написать сообщение
          </Typography>
        </TouchableOpacity>
      );
    }
    if (footer === 'join') {
      return (
        <TouchableOpacity
          activeOpacity={0.9}
          onPress={handleJoinPress}
          style={styles.joinCta}
          accessibilityLabel="Присоединиться к обсуждению"
        >
          <Typography style={styles.joinCtaText}>Хотите Обсудить? Присоединяйтесь :)</Typography>
        </TouchableOpacity>
      );
    }
    return (
      <View style={[styles.composerRow, { backgroundColor: inputSurface, borderColor: inputBorder }]}>
        <TextInput
          value={draft}
          onChangeText={setDraft}
          placeholder="Напишите сообщение…"
          placeholderTextColor={textSecondary}
          style={[styles.composerInput, { color: textPrimary }]}
          onSubmitEditing={handleSend}
          returnKeyType="send"
          multiline={false}
        />
        <TouchableOpacity
          onPress={handleSend}
          disabled={!draft.trim()}
          style={[styles.composerSend, !draft.trim() && { opacity: 0.4 }]}
          accessibilityLabel="Отправить"
        >
          <Ionicons name="arrow-up" size={18} color={ON_ACCENT_TEXT} />
        </TouchableOpacity>
      </View>
    );
  };

  return (
    <View
      // CORE-1253 — парный маркер к `live-chat-panel`: по нему видно, что
      // открылось обсуждение события, а не чат самой практики.
      testID="event-discussion-panel"
      style={[
        styles.container,
        panelFill,
      ]}
    >
      <View style={styles.header}>
        <Typography style={[styles.title, { color: textPrimary }]}>
          Чатик с комментариями
        </Typography>
      </View>

      <ScrollView
        style={styles.messageScroll}
        contentContainerStyle={styles.messageScrollContent}
        showsVerticalScrollIndicator={false}
      >
        {pinnedMessage ? (
          <View style={[styles.pinned, { backgroundColor: 'rgba(233, 130, 69,0.10)', borderColor: 'rgba(233, 130, 69,0.30)' }]}>
            <View style={styles.pinnedHeader}>
              <Ionicons name="pin" size={11} color={ACCENT_TEXT} />
              <Typography style={styles.pinnedBadge}>
                {masterName ? `ОТ ${masterName.toUpperCase()}` : 'ОТ ВЕДУЩЕГО'}
              </Typography>
            </View>
            <Typography style={[styles.pinnedText, { color: textPrimary }]}>{pinnedMessage.text}</Typography>
          </View>
        ) : null}

        {messages.length === 0 && !pinnedMessage && footer !== 'unavailable' ? (
          <View style={styles.empty}>
            <Ionicons
              name="chatbubbles-outline"
              size={28}
              color={WEB ? tc.textMuted : (isDark ? 'rgba(255,255,255,0.28)' : 'rgba(15,20,25,0.28)')}
            />
            <Typography style={[styles.emptyText, { color: textSecondary }]}>
              {canWrite ? 'Напишите первым' : 'Здесь пока тихо'}
            </Typography>
          </View>
        ) : null}

        {messages.map((m) => (
          <View key={m.id} style={styles.message}>
            <View style={styles.messageAvatar}>
              {m.photoUrl ? (
                <Image source={{ uri: m.photoUrl }} style={styles.messageAvatarImg as ImageStyle} />
              ) : (
                <View style={styles.messageAvatarFallback}>
                  <Typography style={styles.messageAvatarInitial}>{m.author.charAt(0)}</Typography>
                </View>
              )}
            </View>
            <View style={{ flex: 1 }}>
              <View style={styles.messageMeta}>
                <Typography
                  style={[
                    styles.messageAuthor,
                    { color: m.isHost ? ACCENT_TEXT : textPrimary },
                  ]}
                >
                  {m.author}
                </Typography>
                {m.isHost ? (
                  <View style={styles.hostBadge}>
                    <Typography style={styles.hostBadgeText}>ВЕДУЩИЙ</Typography>
                  </View>
                ) : null}
                <Typography style={[styles.messageTime, { color: textSecondary }]}>{m.timestamp}</Typography>
              </View>
              <Typography style={[styles.messageText, { color: textPrimary }]}>{m.text}</Typography>
            </View>
          </View>
        ))}
      </ScrollView>

      <View style={styles.footer}>{renderFooter()}</View>
      {sendError ? (
        <View style={styles.errorBanner}>
          <Typography style={styles.errorBannerText}>{sendError}</Typography>
        </View>
      ) : null}
      {joinError && joinVisible ? (
        <View style={styles.errorBanner}>
          <Typography style={styles.errorBannerText}>{joinError}</Typography>
        </View>
      ) : null}

      <JoinEventModal
        visible={joinVisible && !isOwner}
        masterName={masterName ?? null}
        onConfirm={handleJoinConfirm}
        onCancel={() => setJoinVisible(false)}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    borderRadius: 20,
    borderWidth: 1,
    overflow: 'hidden',
    minHeight: 360,
    // Web-only sticky positioning is applied at the call site (parent's
    // layout knows the viewport); the panel itself stays layout-agnostic
    // so it can also be rendered inline on mobile.
  } as ViewStyle,
  header: {
    paddingHorizontal: 20,
    paddingTop: 18,
    paddingBottom: 12,
  } as ViewStyle,
  title: {
    fontSize: 18,
    fontWeight: fontWeights.bold,
    fontFamily: fonts.display,
    letterSpacing: -0.2,
  } as TextStyle,
  messageScroll: {
    flex: 1,
    maxHeight: 480,
  } as ViewStyle,
  messageScrollContent: {
    paddingHorizontal: 20,
    paddingBottom: 16,
  } as ViewStyle,
  pinned: {
    marginBottom: 16,
    padding: 12,
    borderRadius: 14,
    borderWidth: 1,
  } as ViewStyle,
  pinnedHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginBottom: 6,
  } as ViewStyle,
  pinnedBadge: {
    fontSize: 10,
    fontWeight: fontWeights.bold,
    fontFamily: fonts.display,
    color: ACCENT_TEXT,
    letterSpacing: 1.2,
    textTransform: 'uppercase',
    lineHeight: 13,
  } as TextStyle,
  pinnedText: {
    fontSize: 14,
    lineHeight: 20,
    fontWeight: '500',
    fontFamily: fonts.text,
  } as TextStyle,
  empty: {
    alignItems: 'center',
    paddingVertical: 56,
    paddingHorizontal: 24,
    gap: 10,
  } as ViewStyle,
  emptyText: {
    fontSize: 13,
    fontWeight: '500',
    fontFamily: fonts.text,
    textAlign: 'center',
  } as TextStyle,
  message: {
    flexDirection: 'row',
    gap: 10,
    marginBottom: 16,
  } as ViewStyle,
  messageAvatar: {
    width: 30,
    height: 30,
    borderRadius: 15,
    overflow: 'hidden',
    backgroundColor: '#2A2E37',
  } as ViewStyle,
  messageAvatarImg: {
    width: '100%',
    height: '100%',
  } as ImageStyle,
  messageAvatarFallback: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#3A3F4A',
  } as ViewStyle,
  messageAvatarInitial: {
    fontSize: 12,
    fontWeight: fontWeights.bold,
    color: '#FFFFFF',
    fontFamily: fonts.display,
  } as TextStyle,
  messageMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginBottom: 3,
  } as ViewStyle,
  messageAuthor: {
    fontSize: 13,
    fontWeight: fontWeights.bold,
    fontFamily: fonts.display,
    lineHeight: 16,
  } as TextStyle,
  hostBadge: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
    backgroundColor: 'rgba(233, 130, 69,0.16)',
  } as ViewStyle,
  hostBadgeText: {
    fontSize: 9,
    fontWeight: fontWeights.bold,
    fontFamily: fonts.display,
    color: ACCENT_TEXT,
    letterSpacing: 0.7,
    lineHeight: 11,
  } as TextStyle,
  messageTime: {
    fontSize: 11,
    fontWeight: '500',
    fontFamily: fonts.text,
    lineHeight: 14,
  } as TextStyle,
  messageText: {
    fontSize: 14,
    lineHeight: 20,
    fontWeight: '500',
    fontFamily: fonts.text,
  } as TextStyle,
  footer: {
    paddingHorizontal: 16,
    paddingTop: 12,
    paddingBottom: 16,
  } as ViewStyle,
  composerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingLeft: 16,
    paddingRight: 6,
    paddingVertical: 6,
    borderRadius: 22,
    borderWidth: 1,
  } as ViewStyle,
  composerInput: {
    flex: 1,
    height: 36,
    fontSize: 14,
    fontFamily: fonts.text,
    ...(Platform.OS === 'web' && {
      outlineStyle: 'none',
    } as any),
  } as any,
  composerSend: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: '#E98245',
    alignItems: 'center',
    justifyContent: 'center',
  } as ViewStyle,
  banner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    height: 44,
    borderRadius: 22,
    borderWidth: 1,
    ...(Platform.OS === 'web' && { cursor: 'pointer' } as any),
  } as any,
  bannerText: {
    fontSize: 13,
    fontWeight: '600',
    fontFamily: fonts.display,
    letterSpacing: 0.2,
  } as TextStyle,
  joinCta: {
    minHeight: 48,
    borderRadius: 24,
    backgroundColor: '#E98245',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 16,
    paddingVertical: 10,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
      boxShadow: '0 6px 20px rgba(233, 130, 69,0.28)',
    } as any),
  } as any,
  joinCtaText: {
    fontSize: 14,
    fontWeight: fontWeights.bold,
    fontFamily: fonts.display,
    color: ON_ACCENT_TEXT,
    letterSpacing: 0.1,
    textAlign: 'center',
  } as TextStyle,
  errorBanner: {
    paddingHorizontal: 20,
    paddingVertical: 8,
    backgroundColor: 'rgba(220,38,38,0.14)',
    borderTopWidth: 1,
    borderTopColor: 'rgba(220,38,38,0.20)',
  } as ViewStyle,
  errorBannerText: {
    fontSize: 12,
    fontWeight: '600',
    fontFamily: fonts.text,
    color: '#B91C1C',
    textAlign: 'center',
  } as TextStyle,
});

export default LiveCommentsPanel;
